/**
 * @fileoverview Centralized error handlingexport interface ApiErrorData {
  code: ErrorCodeType;
  message: string;
  details?: ErrorDetails;
  timestamp: string;
}

export class ApiError extends Error {
  public readonly code: ErrorCodeType;
  public readonly details: ErrorDetails;
  public readonly timestamp: string;

  constructor(
    code: ErrorCodeType,
    message: string,
    details: ErrorDetails = {}
  ) {
 * Provides structured error types and handling utilities
 */

export const ErrorCode = {
  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  CONNECTION_REFUSED: 'CONNECTION_REFUSED',
  
  // Authentication errors
  UNAUTHORIZED: 'UNAUTHORIZED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  FORBIDDEN: 'FORBIDDEN',
  
  // Validation errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',
  INVALID_FORMAT: 'INVALID_FORMAT',
  
  // Business logic errors
  INSUFFICIENT_BALANCE: 'INSUFFICIENT_BALANCE',
  INVALID_SYMBOL: 'INVALID_SYMBOL',
  MARKET_CLOSED: 'MARKET_CLOSED',
  RATE_LIMITED: 'RATE_LIMITED',
  
  // Server errors
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  NOT_FOUND: 'NOT_FOUND',
  
  // Unknown
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type ErrorCodeType = typeof ErrorCode[keyof typeof ErrorCode];

export interface ErrorDetails {
  [key: string]: unknown;
  status?: number;
  statusText?: string;
  url?: string;
  method?: string;
}

export interface ApiErrorData {
  code: ErrorCodeType;
  message: string;
  details?: ErrorDetails;
  timestamp: string;
}

export class ApiError extends Error implements ApiErrorData {
  public readonly code: ErrorCodeType;
  public readonly details?: ErrorDetails;
  public readonly timestamp: string;

  constructor(
    code: ErrorCodeType,
    message: string,
    details?: ErrorDetails
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
    this.timestamp = new Date().toISOString();
    
    // Maintains proper stack trace for where error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  public toJSON(): ApiErrorData {
    return {
      code: this.code,
      message: this.message,
      details: this.details,
      timestamp: this.timestamp,
    };
  }

  public toString(): string {
    return `ApiError: ${this.code} - ${this.message}`;
  }

  /**
   * Check if this error is retryable
   */
  public isRetryable(): boolean {
    return [
      ErrorCode.NETWORK_ERROR,
      ErrorCode.TIMEOUT_ERROR,
      ErrorCode.CONNECTION_REFUSED,
      ErrorCode.INTERNAL_SERVER_ERROR,
      ErrorCode.SERVICE_UNAVAILABLE,
    ].includes(this.code as any);
  }

  /**
   * Get error severity level
   */
  public getSeverity(): 'low' | 'medium' | 'high' | 'critical' {
    switch (this.code) {
      case ErrorCode.VALIDATION_ERROR:
      case ErrorCode.MISSING_REQUIRED_FIELD:
      case ErrorCode.INVALID_FORMAT:
        return 'low';
      
      case ErrorCode.NETWORK_ERROR:
      case ErrorCode.TIMEOUT_ERROR:
      case ErrorCode.NOT_FOUND:
      case ErrorCode.RATE_LIMITED:
        return 'medium';
      
      case ErrorCode.UNAUTHORIZED:
      case ErrorCode.TOKEN_EXPIRED:
      case ErrorCode.FORBIDDEN:
      case ErrorCode.INSUFFICIENT_BALANCE:
        return 'high';
      
      case ErrorCode.INTERNAL_SERVER_ERROR:
      case ErrorCode.SERVICE_UNAVAILABLE:
      case ErrorCode.CONNECTION_REFUSED:
        return 'critical';
      
      default:
        return 'medium';
    }
  }
}

/**
 * Maps HTTP status codes to error codes
 */
export const mapHttpStatusToErrorCode = (status: number): ErrorCodeType => {
  switch (status) {
    case 400:
      return ErrorCode.VALIDATION_ERROR;
    case 401:
      return ErrorCode.UNAUTHORIZED;
    case 402:
      return ErrorCode.INSUFFICIENT_BALANCE;
    case 403:
      return ErrorCode.FORBIDDEN;
    case 404:
      return ErrorCode.NOT_FOUND;
    case 408:
      return ErrorCode.TIMEOUT_ERROR;
    case 422:
      return ErrorCode.VALIDATION_ERROR;
    case 429:
      return ErrorCode.RATE_LIMITED;
    case 500:
      return ErrorCode.INTERNAL_SERVER_ERROR;
    case 502:
    case 503:
      return ErrorCode.SERVICE_UNAVAILABLE;
    default:
      if (status >= 500) {
        return ErrorCode.INTERNAL_SERVER_ERROR;
      }
      return ErrorCode.UNKNOWN_ERROR;
  }
};

/**
 * Create an ApiError from a Response object
 */
export const createErrorFromResponse = async (
  response: Response,
  url?: string,
  method?: string,
  responseData?: unknown
): Promise<ApiError> => {
  const status = response.status;
  const statusText = response.statusText;
  const code = mapHttpStatusToErrorCode(status);
  
  let message = `HTTP ${status}: ${statusText}`;
  let details: ErrorDetails = { status, statusText, url, method };
  
  // Use provided data or try to parse response
  if (responseData && typeof responseData === 'object') {
    const data = responseData as any;
    message = data.message || data.error || message;
    details = { ...details, ...data };
  } else {
    try {
      const data = await response.json();
      if (data && typeof data === 'object') {
        message = data.message || data.error || message;
        details = { ...details, ...data };
      }
    } catch {
      // Response is not JSON or already consumed, use default message
    }
  }
  
  return new ApiError(code, message, details);
};

/**
 * Create an ApiError from a network error
 */
export const createErrorFromNetworkError = (error: Error): ApiError => {
  if (error.name === 'AbortError') {
    return new ApiError(ErrorCode.TIMEOUT_ERROR, 'Request timeout');
  }
  
  if (error.message.includes('Failed to fetch')) {
    return new ApiError(
      ErrorCode.NETWORK_ERROR,
      'Network error. Please check your connection.'
    );
  }
  
  if (error.message.includes('ECONNREFUSED')) {
    return new ApiError(
      ErrorCode.CONNECTION_REFUSED,
      'Unable to connect to server'
    );
  }
  
  return new ApiError(
    ErrorCode.UNKNOWN_ERROR,
    error.message || 'An unknown error occurred'
  );
};
